/**
 * Flight operations · resource allocation: stands, gates, check-in counters
 * and reclaim belts, as one plan that keeps itself right.
 *
 * Where the established allocators stop — a plan built on the schedule, a
 * list of conflicts, a person to fix them — this desk carries on:
 *
 *  - **It plans on what will happen.** Occupancy comes from the flight
 *    record's best time (actual, predicted, estimated, scheduled) with its
 *    uncertainty, and every back-to-back pair has a CHANCE of meeting, shown
 *    on the bar.
 *  - **It re-plans itself with the fewest moves**, gates following their
 *    stand; what its automation level allows it applies, the rest it
 *    proposes with the reason a person is needed.
 *  - **It says why** — every placement's cost, what it passed over and why,
 *    every resource it could not use.
 *  - **It learns from being overruled**, counted and shown.
 *  - **It answers "what if" and "when"** — a closure or a delay before it
 *    happens, and the cheapest window for maintenance.
 */
import { useMemo, useState } from "react";
import { useSession } from "../../app/SessionContext";
import { useScreenContext } from "../../assistant/screen";
import {
  useAllocAssign,
  useAllocDay,
  useAllocDecide,
  useAllocDeleteRule,
  useAllocPolicy,
  useAllocReplan,
  useAllocSaveRule,
  useAllocWhatIf,
  useAllocWindows,
} from "../../api/queries";
import { Button, Card, EmptyState, ErrorState, Field, Input, Segmented, Select, Skeleton } from "../../design/components";
import {
  KIND_LABEL,
  KIND_NOUN,
  trustAdvice,
  type AllocDay,
  type Assignment,
  type Automation,
  type Demand,
  type EngineResult,
  type Move,
  type Policy,
  type Proposal,
  type Refusal,
  type ResourceKind,
  type Rule,
  type Score,
} from "../../domain/allocation";
import { t, tn } from "../../i18n";
import { tx } from "../../i18n/computed";
import { Page } from "../../layout/AppShell";
import { hhmm as clock } from "../../lib/format";
import { Tiles } from "../executive/shared";
import { FlightTheme } from "./FlightTheme";
import { RegisterTab } from "./RegisterTab";
import s from "./Flights.module.css";
import c from "./Allocation.module.css";

type Tab = "plan" | "whatif" | "automation" | "register";
const hhmm = (iso?: string) => clock(iso, "—");
const MIN = 60_000;
const BASIS: Record<string, string> = { actual: "actual", predicted: "predicted", estimated: "estimated", scheduled: "scheduled" };

export function AllocationPage() {
  const session = useSession();
  const [tab, setTab] = useState<Tab>("plan");
  const day = useAllocDay();
  const d = day.data;

  useScreenContext({
    route: "/allocation",
    title: "Resource allocation",
    summary: d
      ? [
          t("Plan health {h} of 100: {c} conflicts, {u} without a place.", {
            h: d.score.health,
            c: d.score.conflicts,
            u: d.score.unplaced,
          }),
          d.proposal
            ? t("A re-plan is waiting: {n} changes.", { n: d.proposal.moves.length })
            : t("No re-plan is waiting."),
          t("{p}% of passenger stands on a bridge; {n} people by bus.", {
            p: d.score.servedInPlacePct ?? 0,
            n: d.score.peopleBussed,
          }),
        ]
      : [],
    offers: [{ label: t("What is wrong with the plan?"), ask: "what is wrong with today's stand and gate plan, and what would fix it?" }],
    worksInFull: true,
  });

  return (
    <FlightTheme>
      <Page eyebrow={t("{site} · Flight operations", { site: session.site.code })} title="Resource allocation">
        <p className={s.lede}>
          {t(
            "Stands, gates, check-in counters, and baggage belts in one plan, updated when a delay or closure breaks it.",
          )}
        </p>
        {day.isPending && <Skeleton rows={6} />}
        {day.isError && <ErrorState error={day.error} retry={() => void day.refetch()} />}
        {d && !d.demands.some((x) => !x.done) && (
          <EmptyState>
            {t("No flights in the next few hours, so there's nothing to plan. Load the schedule on Flight record.")}
          </EmptyState>
        )}
        {d && (
          <>
            <ScoreTiles score={d.score} pending={d.proposal} sample={d.sample} />
            <Segmented
              ariaLabel="Resource allocation"
              value={tab}
              onChange={setTab}
              options={[
                { value: "plan", label: "Plan" },
                { value: "whatif", label: "What if" },
                { value: "automation", label: "Automation and rules" },
                { value: "register", label: "Register" },
              ]}
            />
            {tab === "plan" && <PlanTab day={d} />}
            {tab === "whatif" && <WhatIfTab day={d} />}
            {tab === "automation" && <AutomationTab day={d} />}
            {tab === "register" && <RegisterTab />}
          </>
        )}
      </Page>
    </FlightTheme>
  );
}

function ScoreTiles({ score, pending, sample }: { score: Score; pending: Proposal | null; sample: boolean }) {
  return (
    <Tiles
      currency="SAR"
      items={[
        {
          key: "health",
          label: t("plan health"),
          value: score.health,
          unit: "",
          text: t("of 100 — conflicts, risk, buses and tows"),
          tone: score.health >= 85 ? "good" : score.health >= 60 ? "warn" : "bad",
          assumed: sample,
          sources: [],
        },
        {
          key: "conflicts",
          label: t("conflicts"),
          value: score.conflicts,
          unit: "",
          text: tn(score.unplaced, "{n} without a place", "{n} without a place"),
          tone: score.conflicts || score.unplaced ? "bad" : "good",
          sources: [],
        },
        {
          key: "risk",
          label: t("expected clashes"),
          value: score.expectedConflicts,
          unit: "",
          text: t("from the predictions' uncertainty"),
          tone: score.expectedConflicts > 1 ? "warn" : "good",
          sources: [],
        },
        {
          key: "bridge",
          label: t("on a bridge"),
          value: score.servedInPlacePct,
          unit: "%",
          text: t("{n} people by bus", { n: score.peopleBussed }),
          sources: [],
        },
        {
          key: "pending",
          label: t("waiting for you"),
          value: pending ? pending.moves.length : 0,
          unit: "",
          text: pending ? t("changes in a proposed re-plan") : t("nothing to decide"),
          tone: pending ? "warn" : "good",
          sources: [],
        },
      ]}
    />
  );
}

/* ── the plan ─────────────────────────────────────────────────────────── */

function PlanTab({ day }: { day: AllocDay }) {
  const [kind, setKind] = useState<ResourceKind>("position");
  const [open, setOpen] = useState<string | null>(null);
  const [preview, setPreview] = useState(false);
  const replan = useAllocReplan();
  const plan = preview && day.proposal ? day.proposal.assignments : day.plan;
  const moved = new Set(preview && day.proposal ? day.proposal.moves.map((m) => m.key) : []);
  const broken = new Set(day.issues.map((i) => i.key));
  return (
    <>
      {day.proposal && <ProposalCard p={day.proposal} preview={preview} setPreview={setPreview} />}
      {day.issues.length > 0 && (
        <Card title="What is wrong with the plan" sub={t("found before anything is moved")} testId="alloc-issues">
          <ul className={c.issues}>
            {day.issues.slice(0, 12).map((i) => (
              <li key={`${i.key}-${i.kind}`} data-k={i.kind}>
                <button type="button" className={c.link} onClick={() => setOpen(i.key)}>
                  {tx(i.why)}
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}
      <Card
        title={KIND_LABEL[kind]}
        sub={t("From one hour ago to ten hours ahead. Color shows the risk of a clash.")}
        testId="alloc-chart"
        actions={
          <div className={c.row}>
            <Segmented
              ariaLabel="Resource"
              value={kind}
              onChange={(k) => {
                setKind(k);
                setOpen(null);
              }}
              options={[
                { value: "position", label: "Stands" },
                { value: "gate", label: "Gates" },
                { value: "counter", label: "Counters" },
                { value: "belt", label: "Belts" },
              ]}
            />
            <Button size="sm" onClick={() => replan.mutate("replan")} disabled={replan.isPending} data-testid="alloc-replan">
              {t("Re-plan now")}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => replan.mutate("plan")} disabled={replan.isPending}>
              {t("Optimize the whole day")}
            </Button>
          </div>
        }
      >
        <Chart day={day} kind={kind} plan={plan} moved={moved} broken={broken} open={open} onOpen={setOpen} />
        <Legend />
      </Card>
      {open && <WhyPanel day={day} dkey={open} onClose={() => setOpen(null)} />}
    </>
  );
}

function Legend() {
  return (
    <div className={c.legend}>
      <span data-r="low">{t("clear")}</span>
      <span data-r="mid">{t("some chance of meeting")}</span>
      <span data-r="high">{t("likely to meet")}</span>
      <span data-r="done">{t("done or fixed")}</span>
      <span data-r="person">{t("placed by a person")}</span>
      <span data-r="tow">{t("towed")}</span>
      <span data-r="closed">{t("closed")}</span>
    </div>
  );
}

function Chart({
  day,
  kind,
  plan,
  moved,
  broken,
  open,
  onOpen,
}: {
  day: AllocDay;
  kind: ResourceKind;
  plan: Assignment[];
  moved: Set<string>;
  broken: Set<string>;
  open: string | null;
  onOpen: (k: string) => void;
}) {
  const now = Date.parse(day.now);
  const start = Math.floor((now - 60 * MIN) / (30 * MIN)) * 30 * MIN;
  const end = start + 11 * 60 * MIN;
  const span = end - start;
  const pct = (t0: number) => Math.max(0, Math.min(100, ((t0 - start) / span) * 100));
  const resources = day.resources.filter((r) => r.kind === kind);
  const demands = useMemo(() => new Map(day.demands.map((d) => [d.key, d])), [day.demands]);
  const threshold = day.policies.find((p) => p.kind === kind)?.riskThreshold ?? 0.3;
  const hours: number[] = [];
  for (let h = Math.ceil(start / (60 * MIN)) * 60 * MIN; h < end; h += 60 * MIN) hours.push(h);
  if (!resources.length)
    return <EmptyState>{t("No {kind} are in the register yet.", { kind: t(KIND_LABEL[kind]).toLowerCase() })}</EmptyState>;
  return (
    <div className={c.chartWrap}>
      <div className={c.chart} style={{ ["--rows" as string]: resources.length }}>
        <div className={c.axis}>
          <div className={c.name} />
          <div className={c.lane}>
            {hours.map((h) => (
              <span key={h} className={c.tick} style={{ insetInlineStart: `${pct(h)}%` }}>
                {hhmm(new Date(h).toISOString())}
              </span>
            ))}
          </div>
        </div>
        {resources.map((r) => (
          <div key={r.code} className={c.line} data-testid={`lane-${r.code}`}>
            <div className={c.name} title={r.name}>
              <b>{r.code}</b>
              {kind === "position" && (
                <small>
                  {r.maxSize}
                  {r.servedInPlace ? " ⌂" : ""}
                </small>
              )}
            </div>
            <div className={c.lane}>
              {hours.map((h) => (
                <span key={h} className={c.grid} style={{ insetInlineStart: `${pct(h)}%` }} />
              ))}
              {(r.closed ?? []).map((cl, i) => {
                const f = cl.from ? Date.parse(cl.from) : start;
                const to = cl.to ? Date.parse(cl.to) : end;
                if (to <= start || f >= end) return null;
                return (
                  <span
                    key={i}
                    className={c.closed}
                    title={`${cl.ref ?? ""} ${cl.why}`}
                    style={{ insetInlineStart: `${pct(f)}%`, width: `${pct(to) - pct(f)}%` }}
                  />
                );
              })}
              {plan.flatMap((a) =>
                a.segments
                  .filter((sg) => sg.codes.includes(r.code))
                  .map((sg, i) => {
                    const dm = demands.get(a.key);
                    if (!dm) return null;
                    const f = Date.parse(sg.from);
                    const to = Date.parse(sg.to);
                    if (to <= start || f >= end) return null;
                    const risk = day.explain[a.key]?.risk ?? 0;
                    const tone = dm.done ? "done" : risk > threshold ? "high" : risk > 0.1 ? "mid" : "low";
                    return (
                      <button
                        type="button"
                        key={`${a.key}-${i}`}
                        className={c.bar}
                        data-r={tone}
                        data-person={a.source === "person" || undefined}
                        data-tow={a.tow || undefined}
                        data-moved={moved.has(a.key) || undefined}
                        data-broken={broken.has(a.key) || undefined}
                        data-open={open === a.key || undefined}
                        data-testid={`bar-${a.key}`}
                        title={`${dm.label} · ${hhmm(sg.from)}–${hhmm(sg.to)}${risk > 0.05 ? ` · ${Math.round(risk * 100)}%` : ""}`}
                        onClick={() => onOpen(a.key)}
                        style={{ insetInlineStart: `${pct(f)}%`, width: `${Math.max(0.8, pct(to) - pct(f))}%` }}
                      >
                        {dm.label.split(" → ")[0]}
                      </button>
                    );
                  }),
              )}
              {now >= start && now <= end && <span className={c.now} style={{ insetInlineStart: `${pct(now)}%` }} />}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProposalCard({ p, preview, setPreview }: { p: Proposal; preview: boolean; setPreview: (v: boolean) => void }) {
  const decide = useAllocDecide();
  const [note, setNote] = useState("");
  return (
    <Card
      title="Proposed re-plan"
      sub={t("{why} · proposed {at}", { why: t(p.trigger), at: hhmm(p.createdAt) })}
      testId="alloc-proposal"
      actions={
        <label className={c.check}>
          <input type="checkbox" checked={preview} onChange={(e) => setPreview(e.target.checked)} /> {t("show it on the chart")}
        </label>
      }
    >
      <ScoreDelta before={p.before} after={p.after} />
      <MoveList moves={p.moves} />
      {!p.decision.apply && p.decision.why.length > 0 && (
        <div className={c.needs}>
          <b>{t("Why it waits for a person:")}</b>
          <ul>
            {p.decision.why.map((w) => (
              <li key={w}>{tx(w)}</li>
            ))}
          </ul>
        </div>
      )}
      <div className={c.row}>
        <Input placeholder={t("note (optional)")} value={note} onChange={(e) => setNote(e.target.value)} className={c.grow} />
        <Button
          variant="primary"
          data-testid="alloc-apply"
          disabled={decide.isPending}
          onClick={() => decide.mutate({ proposalId: p.id, decision: "apply", note: note || undefined })}
        >
          {t("Apply the re-plan")}
        </Button>
        <Button
          variant="ghost"
          disabled={decide.isPending}
          onClick={() => decide.mutate({ proposalId: p.id, decision: "reject", note: note || undefined })}
        >
          {t("Reject")}
        </Button>
      </div>
      {decide.isError && <p className={c.bad}>{String((decide.error as Error).message)}</p>}
    </Card>
  );
}

function ScoreDelta({ before, after }: { before: Score; after: Score }) {
  const rows: [string, number | null, number | null, boolean][] = [
    [t("plan health"), before.health, after.health, true],
    [t("conflicts"), before.conflicts, after.conflicts, false],
    [t("without a place"), before.unplaced, after.unplaced, false],
    [t("expected clashes"), before.expectedConflicts, after.expectedConflicts, false],
    [t("people by bus"), before.peopleBussed, after.peopleBussed, false],
  ];
  return (
    <div className={c.delta}>
      {rows.map(([label, b, a, up]) => {
        const better = b == null || a == null ? null : up ? a > b : a < b;
        const worse = b == null || a == null ? null : up ? a < b : a > b;
        return (
          <div key={label} data-better={better || undefined} data-worse={worse || undefined}>
            <span>{label}</span>
            <b>
              {b ?? "—"} → {a ?? "—"}
            </b>
          </div>
        );
      })}
    </div>
  );
}

function MoveList({ moves }: { moves: Move[] }) {
  if (!moves.length) return <EmptyState>{t("Nothing would change.")}</EmptyState>;
  return (
    <div className={c.scroll}>
      <table className={c.table}>
        <thead>
          <tr>
            <th>{t("Flight")}</th>
            <th>{t("What")}</th>
            <th>{t("From → to")}</th>
            <th>{t("At")}</th>
            <th>{t("Why")}</th>
          </tr>
        </thead>
        <tbody>
          {moves.map((m) => (
            <tr key={`${m.key}-${m.to.join()}`} data-testid={`move-${m.key}`}>
              <td>
                <b>{m.label}</b>
              </td>
              <td>{t(KIND_NOUN[m.kind])}</td>
              <td className={c.mono}>
                {m.from.length ? m.from.join(" › ") : "—"} → <b>{m.to.length ? m.to.join(" › ") : t("none")}</b>
                {m.tow && <span className={c.tag}>{t("tow")}</span>}
              </td>
              <td className={c.mono}>{hhmm(m.at)}</td>
              <td className={c.why}>
                {tx(m.why)}
                {m.impact && <div className={c.impact}>{tx(m.impact)}</div>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── why here ─────────────────────────────────────────────────────────── */

function WhyPanel({ day, dkey, onClose }: { day: AllocDay; dkey: string; onClose: () => void }) {
  const d = day.demands.find((x) => x.key === dkey);
  const a = day.plan.find((x) => x.key === dkey);
  const e = day.explain[dkey];
  const assign = useAllocAssign();
  const [to, setTo] = useState("");
  const [note, setNote] = useState("");
  const [refused, setRefused] = useState<Refusal[] | null>(null);
  if (!d) return null;
  const options = day.resources.filter((r) => r.kind === d.kind);
  const place = (force: boolean) => {
    if (!to) return;
    const codes = d.kind === "counter" ? runFrom(day, to, d.units) : [to];
    assign.mutate(
      { key: d.key, codes, note: note || undefined, force },
      { onSuccess: (r) => setRefused(r.refused.length && !force ? r.refused : null) },
    );
  };
  const grouped = groupRefusals(e?.refusals ?? []);
  return (
    <Card
      title={t("Why {what} is where it is", { what: d.label })}
      sub={<Times d={d} />}
      testId="alloc-why"
      actions={
        <Button size="sm" variant="ghost" onClick={onClose}>
          {t("Close")}
        </Button>
      }
    >
      <div className={c.why2}>
        <section>
          <h4>{t("Placed on")}</h4>
          <p className={c.big}>
            {a ? a.segments.map((sg) => sg.codes.join("+")).join(" › ") : t("nothing yet")}
            {a?.source === "person" && <span className={c.tag}>{t("by a person")}</span>}
            {a?.tow && <span className={c.tag}>{t("tow")}</span>}
          </p>
          {e && (
            <>
              <h4>{t("What it costs, and why")}</h4>
              {e.terms.length ? (
                <ul className={c.terms}>
                  {e.terms.map((x, i) => (
                    <li key={i} data-neg={x.cost < 0 || undefined}>
                      <span>{tx(x.text)}</span>
                      <b>{x.cost > 0 ? `+${x.cost}` : x.cost}</b>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className={c.dim}>{t("Nothing counts against it.")}</p>
              )}
              {e.risk > 0.05 && (
                <p className={c.dim}>
                  {t("{pct}% chance of meeting {who}, from how sure both times are.", {
                    pct: Math.round(e.risk * 100),
                    who: e.riskWith ?? "",
                  })}
                </p>
              )}
            </>
          )}
        </section>
        <section>
          <h4>{t("What it passed over")}</h4>
          {e?.alternatives.length ? (
            <ul className={c.alts}>
              {e.alternatives.map((x) => (
                <li key={x.codes.join()}>
                  <b>{x.codes.join("+")}</b> <span className={c.dim}>{tx(x.worse)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className={c.dim}>{t("Nowhere else it could go.")}</p>
          )}
          {grouped.length > 0 && (
            <>
              <h4>{t("Could not go on")}</h4>
              <ul className={c.refusals}>
                {grouped.map(([k, list]) => (
                  <li key={k}>
                    <b>{t(REFUSAL_LABEL[k] ?? k)}</b>{" "}
                    <span className={c.dim}>
                      {[...new Set(list.map((r) => r.code))].slice(0, 8).join(", ")}
                      {list.length > 8 ? " …" : ""}
                    </span>
                    {(k === "closed" || k === "occupied" || k === "rule" || k === "link") && (
                      <div className={c.dim}>{tx(list[0].why)}</div>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
        {!d.done && (
          <section>
            <h4>{t("Put it somewhere else")}</h4>
            <div className={c.row}>
              <Select value={to} onChange={(ev) => setTo(ev.target.value)} aria-label={t("Resource")}>
                <option value="">{t("choose…")}</option>
                {options.map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.code}
                    {r.name && r.name !== r.code ? ` — ${r.name}` : ""}
                  </option>
                ))}
              </Select>
              <Input placeholder={t("why (optional)")} value={note} onChange={(ev) => setNote(ev.target.value)} className={c.grow} />
              <Button onClick={() => place(false)} disabled={!to || assign.isPending} data-testid="alloc-place">
                {t("Place it")}
              </Button>
            </div>
            <p className={c.dim}>
              {t("The engine remembers where people put each airline, and prefers it next time.")}
            </p>
            {refused && (
              <div className={c.needs}>
                <b>{t("Refused:")}</b>
                <ul>
                  {refused.map((r, i) => (
                    <li key={i}>{tx(r.why)}</li>
                  ))}
                </ul>
                <Button variant="danger" size="sm" onClick={() => place(true)}>
                  {t("Place it anyway")}
                </Button>
              </div>
            )}
            {assign.isSuccess && !refused && <p className={c.ok}>{t("Placed. The plan and its checks are updated.")}</p>}
          </section>
        )}
      </div>
    </Card>
  );
}

const REFUSAL_LABEL: Record<string, string> = {
  too_big: "too small",
  occupied: "taken",
  neighbour: "blocked by a neighbour",
  closed: "closed",
  link: "not reachable from its stand",
  use: "not for this kind of flight",
  rule: "ruled out by a rule",
  none: "not in the register",
};

function groupRefusals(rs: Refusal[]): [string, Refusal[]][] {
  const m = new Map<string, Refusal[]>();
  for (const r of rs) m.set(r.kind, [...(m.get(r.kind) ?? []), r]);
  return [...m.entries()];
}

function runFrom(day: AllocDay, code: string, units: number): string[] {
  const r = day.resources.find((x) => x.code === code);
  const group = day.resources
    .filter((x) => x.kind === "counter" && x.group === r?.group)
    .sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));
  const i = group.findIndex((x) => x.code === code);
  return group.slice(i, i + units).map((x) => x.code);
}

function Times({ d }: { d: Demand }) {
  return (
    <span>
      {t(KIND_NOUN[d.kind])} · {hhmm(d.from)} ({t(BASIS[d.basisStart])}
      {d.sigmaStart > 1 ? ` ±${Math.round(d.sigmaStart)}` : ""}) – {hhmm(d.to)} ({t(BASIS[d.basisEnd])}
      {d.sigmaEnd > 1 ? ` ±${Math.round(d.sigmaEnd)}` : ""}) · {t("code {s}", { s: d.size })} · {tn(d.load, "{n} person", "{n} people")}
      {d.kind === "counter" ? ` · ${tn(d.units, "{n} desk", "{n} desks")}` : ""}
    </span>
  );
}

/* ── what if, and when ────────────────────────────────────────────────── */

function WhatIfTab({ day }: { day: AllocDay }) {
  const whatIf = useAllocWhatIf();
  const windows = useAllocWindows();
  const [mode, setMode] = useState<"close" | "delay">("close");
  const [code, setCode] = useState("");
  const [fromH, setFromH] = useState(1);
  const [hours, setHours] = useState(3);
  const [flight, setFlight] = useState("");
  const [late, setLate] = useState(45);
  const [wCode, setWCode] = useState("");
  const [wHours, setWHours] = useState(4);
  const now = Date.parse(day.now);
  const flights = day.demands.filter((d) => d.kind === "position" && !d.done);
  const run = () => {
    if (mode === "close" && code) {
      const from = new Date(now + fromH * 3_600_000).toISOString();
      whatIf.mutate({ kind: "close", code, from, to: new Date(Date.parse(from) + hours * 3_600_000).toISOString() });
    } else if (mode === "delay" && flight) {
      whatIf.mutate({ kind: "delay", movementId: flight, minutes: late });
    }
  };
  return (
    <>
      <Card title="What would it do if…" sub={t("Test a closure or delay. Nothing is saved.")} testId="alloc-whatif">
        <Segmented
          ariaLabel="What if"
          value={mode}
          onChange={setMode}
          options={[
            { value: "close", label: "A resource closes" },
            { value: "delay", label: "A flight runs late" },
          ]}
        />
        <div className={c.form}>
          {mode === "close" ? (
            <>
              <Field label="Resource">
                <ResourceSelect day={day} value={code} onChange={setCode} />
              </Field>
              <Field label="Starting in (hours)">
                <Input type="number" min={0} max={20} value={fromH} onChange={(e) => setFromH(Number(e.target.value))} />
              </Field>
              <Field label="For (hours)">
                <Input type="number" min={1} max={24} value={hours} onChange={(e) => setHours(Number(e.target.value))} />
              </Field>
            </>
          ) : (
            <>
              <Field label="Flight">
                <Select value={flight} onChange={(e) => setFlight(e.target.value)}>
                  <option value="">{t("choose…")}</option>
                  {flights.map((d) => (
                    <option key={d.key} value={d.movementIds[0]}>
                      {d.label} · {hhmm(d.from)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Minutes late">
                <Input type="number" min={5} max={600} value={late} onChange={(e) => setLate(Number(e.target.value))} />
              </Field>
            </>
          )}
          <Button variant="primary" onClick={run} disabled={whatIf.isPending} data-testid="alloc-whatif-run">
            {t("Show me")}
          </Button>
        </div>
        {whatIf.data && <WhatIfResult r={whatIf.data} />}
      </Card>
      <Card
        title="The best time for maintenance"
        sub={t("Best times for the closure over the next day")}
        testId="alloc-windows"
      >
        <div className={c.form}>
          <Field label="Resource">
            <ResourceSelect day={day} value={wCode} onChange={setWCode} />
          </Field>
          <Field label="Hours of work">
            <Input type="number" min={1} max={12} value={wHours} onChange={(e) => setWHours(Number(e.target.value))} />
          </Field>
          <Button
            variant="primary"
            disabled={!wCode || windows.isPending}
            onClick={() => windows.mutate({ code: wCode, minutes: wHours * 60 })}
            data-testid="alloc-windows-run"
          >
            {t("Find the windows")}
          </Button>
        </div>
        {windows.isPending && <Skeleton rows={3} />}
        {windows.data && (
          <ol className={c.windows}>
            {windows.data.map((w, i) => (
              <li key={w.from} data-best={i === 0 || undefined}>
                <b className={c.mono} dir="ltr">
                  {hhmm(w.from)}–{hhmm(w.to)}
                </b>{" "}
                <span>{tx(w.text)}</span>
              </li>
            ))}
          </ol>
        )}
      </Card>
    </>
  );
}

function ResourceSelect({ day, value, onChange }: { day: AllocDay; value: string; onChange: (v: string) => void }) {
  return (
    <Select value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{t("choose…")}</option>
      {(["position", "gate", "counter", "belt"] as ResourceKind[]).map((k) => (
        <optgroup key={k} label={t(KIND_LABEL[k])}>
          {day.resources
            .filter((r) => r.kind === k)
            .map((r) => (
              <option key={r.code} value={r.code}>
                {r.code}
                {r.name && r.name !== r.code ? ` — ${r.name}` : ""}
              </option>
            ))}
        </optgroup>
      ))}
    </Select>
  );
}

function WhatIfResult({ r }: { r: EngineResult }) {
  return (
    <div className={c.result} data-testid="alloc-whatif-result">
      <p className={c.dim}>{t("With the change and nothing moved → after the engine re-plans around it:")}</p>
      {r.before && <ScoreDelta before={r.before} after={r.score} />}
      <MoveList moves={r.moves} />
      {r.unplaced.length > 0 && (
        <ul className={c.issues}>
          {r.unplaced.map((u) => (
            <li key={u.key} data-k="unplaced">
              {tx(u.why)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ── automation and rules ─────────────────────────────────────────────── */

function AutomationTab({ day }: { day: AllocDay }) {
  const advice = trustAdvice(day.trust);
  return (
    <>
      <Card
        title="How much it does on its own"
        sub={t("For each resource: manual, suggest only, or automatic")}
        testId="alloc-policies"
      >
        <div className={c.policies}>
          {day.policies.map((p) => (
            <PolicyEditor key={p.kind} p={p} />
          ))}
        </div>
        <p className={c.dim}>
          {t(
            "Even on automatic, it never tows, changes a gate after boarding is announced, moves passengers from a bridge to a bus, or makes the plan worse.",
          )}
        </p>
        <div className={c.trust}>
          <span>{t("{n} re-plans proposed", { n: day.trust.proposed })}</span>
          <span>{t("{n} applied by a person", { n: day.trust.applied })}</span>
          <span>{t("{n} rejected", { n: day.trust.rejected })}</span>
          <span>{t("{n} applied automatically", { n: day.trust.auto })}</span>
        </div>
        {advice && <p className={c.ok}>{advice}</p>}
      </Card>
      <RulesCard day={day} />
      <Card title="What it has learned" sub={t("How often people overrode its choice")} testId="alloc-learned">
        {day.learned.length ? (
          <ul className={c.alts}>
            {day.learned.map((l) => (
              <li key={`${l.kind}-${l.operator}-${l.code}`}>
                <b>
                  {l.operator} → {l.code}
                </b>{" "}
                <span className={c.dim}>
                  {t(KIND_NOUN[l.kind])} · {tn(l.n, "{n} time", "{n} times")}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState>{t("Nothing yet.")}</EmptyState>
        )}
      </Card>
      <Card title="Every change" sub={t("who or what moved which flight, and why")} testId="alloc-log">
        {day.log.length ? (
          <div className={c.scroll}>
            <table className={c.table}>
              <thead>
                <tr>
                  <th>{t("When")}</th>
                  <th>{t("Flight")}</th>
                  <th>{t("What")}</th>
                  <th>{t("From → to")}</th>
                  <th>{t("By")}</th>
                  <th>{t("Why")}</th>
                </tr>
              </thead>
              <tbody>
                {day.log.slice(0, 40).map((l, i) => (
                  <tr key={i}>
                    <td className={c.mono}>{hhmm(l.at)}</td>
                    <td>
                      <b>{l.label}</b>
                    </td>
                    <td>{t(KIND_NOUN[l.kind])}</td>
                    <td className={c.mono}>
                      {l.from.join(" › ") || "—"} → {l.to.join(" › ") || "—"}
                    </td>
                    <td>{l.source === "auto" ? t("the engine, automatically") : l.by ?? t(l.source)}</td>
                    <td className={c.why}>{tx(l.why)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState>{t("No changes yet today.")}</EmptyState>
        )}
      </Card>
    </>
  );
}

function PolicyEditor({ p }: { p: Policy }) {
  const save = useAllocPolicy();
  const [v, setV] = useState(p);
  const dirty = JSON.stringify(v) !== JSON.stringify(p);
  return (
    <div className={c.policy} data-testid={`policy-${p.kind}`}>
      <h4>{t(KIND_LABEL[p.kind])}</h4>
      <Segmented<Automation>
        ariaLabel="Automation"
        value={v.automation}
        onChange={(a) => setV({ ...v, automation: a })}
        options={[
          { value: "off", label: "By hand" },
          { value: "advise", label: "Advise" },
          { value: "auto", label: "Automatic" },
        ]}
      />
      <div className={c.nums}>
        <Field label="Buffer (min)">
          <Input type="number" min={0} max={60} value={v.bufferMin} onChange={(e) => setV({ ...v, bufferMin: Number(e.target.value) })} />
        </Field>
        <Field label="Freeze (min)">
          <Input type="number" min={0} max={240} value={v.freezeMin} onChange={(e) => setV({ ...v, freezeMin: Number(e.target.value) })} />
        </Field>
        <Field label="Risk limit (%)">
          <Input
            type="number"
            min={5}
            max={95}
            value={Math.round(v.riskThreshold * 100)}
            onChange={(e) => setV({ ...v, riskThreshold: Number(e.target.value) / 100 })}
          />
        </Field>
        <Field label="Most changes alone">
          <Input type="number" min={0} max={20} value={v.maxAutoMoves} onChange={(e) => setV({ ...v, maxAutoMoves: Number(e.target.value) })} />
        </Field>
      </div>
      <Button size="sm" disabled={!dirty || save.isPending} onClick={() => save.mutate(v)}>
        {t("Save")}
      </Button>
    </div>
  );
}

function RulesCard({ day }: { day: AllocDay }) {
  const save = useAllocSaveRule();
  const del = useAllocDeleteRule();
  const [draft, setDraft] = useState<Rule>({ id: "", kind: "position", codes: [], effect: "prefer", note: "" });
  const [codes, setCodes] = useState("");
  return (
    <Card title="Rules" sub={t("Airport rules for stands, gates, and desks")} testId="alloc-rules">
      <div className={c.scroll}>
        <table className={c.table}>
          <thead>
            <tr>
              <th>{t("For")}</th>
              <th>{t("Effect")}</th>
              <th>{t("On")}</th>
              <th>{t("Why")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {day.rules.map((r) => (
              <tr key={r.id}>
                <td>
                  {t(KIND_LABEL[r.kind])}
                  {r.operator ? ` · ${r.operator}` : ""}
                  {r.sizes?.length ? ` · ${t("code")} ${r.sizes.join("/")}` : ""}
                  {r.serviceType ? ` · ${t(r.serviceType)}` : ""}
                </td>
                <td>
                  <b>{t(r.effect)}</b>
                  {r.weight && (r.effect === "prefer" || r.effect === "avoid") ? ` (${r.weight})` : ""}
                </td>
                <td className={c.mono}>{r.codes.join(", ")}</td>
                <td className={c.why}>{r.note}</td>
                <td>
                  <Button size="sm" variant="ghost" onClick={() => del.mutate(r.id)}>
                    {t("Remove")}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className={c.form}>
        <Field label="Resource">
          <Select value={draft.kind} onChange={(e) => setDraft({ ...draft, kind: e.target.value as ResourceKind })}>
            {(["position", "gate", "counter", "belt"] as ResourceKind[]).map((k) => (
              <option key={k} value={k}>
                {t(KIND_LABEL[k])}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Airline (optional)">
          <Input value={draft.operator ?? ""} maxLength={3} onChange={(e) => setDraft({ ...draft, operator: e.target.value.toUpperCase() || undefined })} />
        </Field>
        <Field label="Effect">
          <Select value={draft.effect} onChange={(e) => setDraft({ ...draft, effect: e.target.value as Rule["effect"] })}>
            <option value="prefer">{t("prefer")}</option>
            <option value="avoid">{t("avoid")}</option>
            <option value="require">{t("require")}</option>
            <option value="forbid">{t("forbid")}</option>
          </Select>
        </Field>
        <Field label="Codes (comma; @A for a group)">
          <Input value={codes} onChange={(e) => setCodes(e.target.value)} />
        </Field>
        <Field label="Why">
          <Input value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} />
        </Field>
        <Button
          variant="primary"
          disabled={!codes.trim() || !draft.note.trim() || save.isPending}
          onClick={() =>
            save.mutate(
              { ...draft, codes: codes.split(",").map((x) => x.trim()).filter(Boolean) },
              {
                onSuccess: () => {
                  setCodes("");
                  setDraft({ ...draft, note: "" });
                },
              },
            )
          }
        >
          {t("Add the rule")}
        </Button>
      </div>
    </Card>
  );
}
